import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CollisionPriority } from '@dnd-kit/abstract';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useDroppable } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { IconButton } from 'design-system';
import { ChevronLeft, ChevronRight, GripVertical, Move, Plus, X } from 'lucide-react';

import { ChartProvider } from '@/features/charts/context/ChartContext';
import { CsvDownloadProvider } from '@/features/charts/context/CsvTableContext';
import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import { getDefinitionForWidget } from '@/features/dashboard/components/apps';
import {
  DrawerStep,
  useDrawerSteps
} from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import { useRemoveWidgetContext } from '@/features/dashboard/state/RemoveWidgetContext';
import { MarketWidget, MarketWidgetProvider } from '@/features/markets';
import type { BaseTabType, MarketsComponentType } from '@/features/markets';

import { useDashboardEditActions } from '../../../hooks/useDashboardEditActions';
import { Item } from './Item';
import { MoveMenu } from './MoveMenu';

import type { ColumnProps } from '../types';

/**
 * Column component representing an app container with tabs.
 * Supports drag-and-drop reordering and contains a preview of the widget.
 */
export const Column = ({
  id,
  index,
  items,
  onRemoveApp,
  onRemoveItem,
  container,
  currentDashboardName,
  currentTabId,
  app,
  onTabCreated,
  preview,
  isDragging
}: ColumnProps) => {
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [moveAppMenuAnchor, setMoveAppMenuAnchor] = useState<HTMLElement | null>(null);
  const [canScrollStart, setCanScrollStart] = useState(false);
  const [canScrollEnd, setCanScrollEnd] = useState(false);
  const actions = useDashboardEditActions();

  const { ref } = useSortable({
    id,
    index,
    handle: handleRef,
    type: 'column',
    collisionPriority: CollisionPriority.Low,
    accept: ['column'],
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  const { ref: itemDropRef } = useDroppable({
    id: `${id}-item-drop`,
    type: 'column-item-drop',
    accept: ['item', 'singleItem'],
    collisionPriority: CollisionPriority.Low,
    data: { columnId: id }
  });

  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();
  const { setInitialValues } = useAppForm();

  const tabContent = useMemo((): BaseTabType | undefined => {
    const tabId = items[0]?.id;
    if (!tabId) return undefined;

    const components = preview?.components as MarketsComponentType[] | undefined;
    const appWithTab = components?.find(component => {
      return 'tabs' in component && component.tabs?.some((tab: BaseTabType) => tab.id === tabId);
    });

    return appWithTab?.tabs?.find((tab: BaseTabType) => tab.id === tabId);
  }, [preview, items]);

  const handleEdit = useCallback(
    (tabId: string) => {
      const tab = items.find(t => t.id === tabId);
      if (!tab) return;
      const definition = getDefinitionForWidget(tab);
      const values = definition.fromWidget(tab);
      setIsEditMode(true);
      setEditingTarget({ appId: id, tabId });
      setInitialValues(values);
      setDrawerStep(definition.drawerStep);
    },
    [items, setIsEditMode, setEditingTarget, id, setInitialValues, setDrawerStep]
  );

  const handleRenameItem = useCallback(
    (itemId: string, name: string) => {
      if (!currentTabId) return;
      actions.renameAppTab(currentTabId, id, itemId, name);
    },
    [currentTabId, id, actions]
  );

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollStart(el.scrollLeft > 1);
    setCanScrollEnd(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  // Keep the custom tab strip's scroll affordances in sync with its overflow.
  useEffect(() => {
    updateScrollState();

    const el = scrollerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    el.addEventListener('scroll', updateScrollState, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', updateScrollState);
    };
  }, [updateScrollState]);

  // Recompute when tabs are added/removed or drag ends.
  useEffect(() => {
    updateScrollState();
  }, [items, isDragging, updateScrollState]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    scrollerRef.current?.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
  }, []);

  const { openRemoveModal } = useRemoveWidgetContext();

  return (
    <div
      ref={ref}
      className='relative overflow-hidden border-4 border-dashed border-gx-text-disabled bg-gx-tile-dark pt-0.5'
    >
      <div className='flex gap-0.5'>
        <IconButton
          ref={handleRef}
          className='h-[46px] w-6 cursor-grab rounded-none'
          label='Drag to reorder app'
          variant='ghost'
        >
          <GripVertical aria-hidden='true' />
        </IconButton>
        <div className='flex min-w-0 flex-1 overflow-hidden'>
          {canScrollStart && (
            <IconButton
              className='h-[46px] w-8 min-w-8 rounded-none bg-gx-tile-light text-gx-text-disabled hover:bg-gx-response hover:text-white'
              label='Scroll tabs left'
              variant='ghost'
              onClick={() => {
                scroll('left');
              }}
            >
              <ChevronLeft aria-hidden='true' />
            </IconButton>
          )}
          <div
            ref={scrollerRef}
            aria-label='tabs'
            className='mr-0.5 flex min-w-0 flex-1 overflow-x-auto'
            role='tablist'
          >
            {items.map((item, idx) => (
              <Item
                key={item.id}
                column={id}
                container={container}
                currentDashboardName={currentDashboardName}
                currentTabId={currentTabId}
                index={idx}
                isSingleItem={items.length === 1}
                item={item}
                onEdit={handleEdit}
                onRemoveItem={onRemoveItem}
                onRename={name => {
                  handleRenameItem(item.id, name);
                }}
                onTabCreated={onTabCreated}
              />
            ))}
          </div>
          {canScrollEnd && (
            <IconButton
              className='h-[46px] w-8 min-w-8 rounded-none bg-gx-tile-light text-gx-text-disabled hover:bg-gx-response hover:text-white'
              label='Scroll tabs right'
              variant='ghost'
              onClick={() => {
                scroll('right');
              }}
            >
              <ChevronRight aria-hidden='true' />
            </IconButton>
          )}
          <IconButton
            className='h-[46px] w-8 rounded-none bg-gx-tile-light text-gx-text-disabled'
            label='Add new app'
            variant='ghost'
            onClick={() => {
              setIsEditMode(true);
              // appId-only signals "add a new tab inside this widget" (see DrawerStepsContext).
              setEditingTarget({ appId: id });
              setDrawerStep(DrawerStep.SelectApp);
            }}
          >
            <Plus aria-hidden='true' />
          </IconButton>
        </div>
        <div className='flex gap-0.5'>
          <IconButton
            className='h-[46px] rounded-none'
            label='Move to ...'
            variant='ghost'
            onClick={e => {
              setMoveAppMenuAnchor(e.currentTarget);
            }}
          >
            <Move aria-hidden='true' />
          </IconButton>
          <IconButton
            className='h-[46px] rounded-none'
            label='Remove app'
            variant='ghost'
            onClick={() => {
              openRemoveModal(() => {
                onRemoveApp(id);
              });
            }}
          >
            <X aria-hidden='true' />
          </IconButton>
          <MoveMenu
            anchorEl={moveAppMenuAnchor}
            app={app}
            open={Boolean(moveAppMenuAnchor)}
            variant='app'
            onClose={() => {
              setMoveAppMenuAnchor(null);
            }}
            onRemoveFromCurrent={() => {
              onRemoveApp(id);
            }}
          />
        </div>
      </div>
      <div
        ref={itemDropRef}
        className='relative mt-3 flex-1'
      >
        {tabContent && (
          <MarketWidgetProvider>
            <ChartProvider>
              <CsvDownloadProvider>
                <MarketWidget tab={tabContent} />
              </CsvDownloadProvider>
            </ChartProvider>
          </MarketWidgetProvider>
        )}
        <div className='absolute inset-0 z-[var(--z-dragging-group,20)] bg-[#121836]/80' />
      </div>
    </div>
  );
};
